import React, { useEffect, useImperativeHandle, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import { ComboboxQuestionModelTypes, ComboboxSubmitResponse, IComboboxResponseValidation } from './types'

import useViewport from '../../modules/viewport/useViewport'
import useForceImmutable from '@aytm/lib/hooks/useForceImmutable'
import useSession from '../../session/useSession'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme'
import commonStyle from '@aytm/app/swidget/base/questions/_common/style.module.scss'
import useInterfaceLocale from '@aytm/app/swidget/base/modules/locale/useInterfaceLocale'
import { DEFAULT_WIDGET_WIDTH } from '@aytm/app/swidget/base/modules/viewport/types.ts'

/* eslint-disable @typescript-eslint/no-unsafe-assignment,
@typescript-eslint/no-explicit-any,
@typescript-eslint/no-unsafe-member-access,
@typescript-eslint/no-unsafe-call,
@typescript-eslint/no-var-requires */
const CX = require('../../../questions/components/scx')
const OldComboboxQuestion: any = CX.default

const ComboboxQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<ComboboxQuestionModelTypes>>(({ model }, ref) => {
    const [[__response, responseValidation, __frame], setResponse] = useState<
      [ComboboxSubmitResponse, IComboboxResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const viewport = useViewport()
    const theme = useTheme()
    const { localeID } = useInterfaceLocale()

    const [showValidation, setShowValidation] = useState(false)

    useEffect(() => {
      session.enableNext(responseValidation?.valid)
    }, [session, responseValidation])

    const onUpdateResponse = (answer: any, validation: any) => {
      model.response = answer.reduce((prev: any, cur: any) => {
        if (cur.get('id'))
          prev.push(
            cur.get('oe') && cur.get('comment') ? { id: cur.get('id'), c: cur.get('comment') } : { id: cur.get('id') }
          )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return prev
      }, [])

      setResponse([model.response, { valid: validation.get('valid') }, model.frame])
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(
      ref,
      () => {
        return {
          tryNext() {
            if (model.responseValidation?.valid || session.skipValidation) {
              return false
            } else {
              session.enableNext(false)
              return true
            }
          },
          tryPrev() {
            return false
          },
          showValidation() {
            setShowValidation(true)
          }
        }
      },
      [model, session]
    )

    const immutableModelSchema = useForceImmutable(model.schema)

    return (
      <QuestionFrame model={model} bodyClassName={commonStyle.mobileView}>
        <div className={commonStyle.oldQuestion}>
          <OldComboboxQuestion
            questionSchema={immutableModelSchema}
            answerChanged={onUpdateResponse}
            theme={viewport.device}
            noHeader={true}
            preview={session.action.preview != null}
            width={DEFAULT_WIDGET_WIDTH.DESKTOP}
            accentColor={theme.style.accentColor}
            showErrors={showValidation && !responseValidation.valid}
            cxUrl={session.urls?.cx_data}
            validateUrl={session.urls?.validate_geo}
            data={{ proxy_params: session.action.action_proxy_params }}
            locale={session.action?.data?.locale?.survey_locale || localeID}
          />
        </div>
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

ComboboxQuestion.displayName = 'ComboboxQuestion'

export default ComboboxQuestion
